import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { cn } from '@/core/lib/utils';
import { useInstantFormat } from '@/shared/hooks/useInstantFormat';
import type { OverdueTaskDto } from '../../types';

interface OverdueTasksTableRowProps {
  task: OverdueTaskDto;
}

const PRIORITY_LABELS = ['none', 'low', 'medium', 'high', 'urgent'] as const;

const PRIORITY_STYLES: Record<number, string> = {
  0: 'bg-muted text-muted-foreground',
  1: 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400',
  2: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-400',
  3: 'bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-400',
  4: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400',
};

function getInitials(name: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

export function OverdueTasksTableRow({ task }: OverdueTasksTableRowProps) {
  const { t } = useTranslation('analytics');
  const formatInstant = useInstantFormat();

  const priorityKey = PRIORITY_LABELS[task.priority] ?? 'none';
  const priorityStyle = PRIORITY_STYLES[task.priority] ?? PRIORITY_STYLES[0];

  // Analytics dueDate is a plain date-time Instant (NOT a PlannedDate) — render in
  // the user's timezone/format, not with formatPlannedDate.
  const formattedDueDate = task.dueDate ? formatInstant(task.dueDate) : '—';

  return (
    <tr className="border-b last:border-0 hover:bg-accent/30">
      {/* Task */}
      <td className="max-w-[220px] py-3 pl-4 pr-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />
          <span className="line-clamp-2 text-sm font-semibold">{task.title ?? '—'}</span>
        </div>
      </td>

      {/* Project */}
      <td className="max-w-[160px] px-2 py-3">
        <div className="flex items-center gap-1.5">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: task.projectColor ?? '#6b7280' }}
          />
          <span className="min-w-0 truncate text-sm text-muted-foreground">
            {task.projectName ?? '—'}
          </span>
        </div>
      </td>

      {/* Assignee */}
      <td className="max-w-[160px] px-2 py-3">
        <div className="flex items-center gap-2">
          <Avatar className="h-6 w-6 shrink-0">
            <AvatarImage src={task.assigneeAvatarUrl ?? undefined} />
            <AvatarFallback className="text-xs">{getInitials(task.assigneeName)}</AvatarFallback>
          </Avatar>
          <span className="min-w-0 truncate text-sm">{task.assigneeName ?? '—'}</span>
        </div>
      </td>

      {/* Priority */}
      <td className="px-2 py-3">
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
            priorityStyle,
          )}
        >
          {t(`priority.${priorityKey}`)}
        </span>
      </td>

      {/* Due date */}
      <td className="px-2 py-3 text-sm font-medium text-destructive">{formattedDueDate}</td>

      {/* Delay */}
      <td className="px-2 py-3 pr-4">
        <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-600 dark:bg-red-950 dark:text-red-400">
          +{task.delayDays}d
        </span>
      </td>
    </tr>
  );
}
